<script setup lang="ts">
import UIToolTip from '@/components/ui/UIToolTip.vue'
import { useDetectBreakpoint } from '@/composables/useDetectBreakpoint'
import {
  TTooltipAlignment,
  TTooltipDirection,
  TTooltipVariant
} from '@lifk/types'
import { PhQuestion } from '@phosphor-icons/vue'
import { computed, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    /** What text should be displayed in the button's tooltip? (The tooptip will not be displayed here, only in the actual application.) */
    tooltipText: string
    /** In which direction should the tooltip appear with respect to the button? */
    tooltipDirection: TTooltipDirection
    /** How should the tooltip be aligned with respect to the button? */
    tooltipAlignment: TTooltipAlignment
    /** What text size should be used in the tooltip? */
    tooltipTextSize?: 'auto' | 'small' | 'default'
    /** What maximum width (in pixels) should the tooltip wrap at? */
    tooltipMaxWidthPx?: number
    /** Should the tooltip also be available on mobile, where a tap toggles it?
     * (Recommended to only use this if the button does not perform any other action.) */
    tooltipOnMobile?: boolean
    /** What tooltip background color should be used? */
    tooltipVariant?: TTooltipVariant
    /** Should there be a delay when rolling off of the target element that prevents the tooltip from hiding right away? (Any value triggers the delay.) */
    tooltipDelayTag?: string
    /** What accessible label should be used, instead of the tooltip text? */
    label?: string
    /** What variant of the button should be used? */
    variant?: keyof typeof variantMapping
    /** Is the button disabled? */
    disabled?: boolean
    /** What HTML control type should be applied to the button? */
    buttonType?: 'button' | 'submit'
    /** Does the button have a popup (e.g. a menu)? (Used to set aria attributes) */
    hasPopup?: boolean
    /** Is the content this button controls currently expanded? (sets aria-expanded) */
    expanded?: boolean
    /** Is the button a toggle that is currently on? (sets aria-pressed) */
    selected?: boolean
    /** The id of the element this button controls (sets aria-controls) */
    ariaControls?: string
  }>(),
  {
    variant: 'default',
    buttonType: 'button',
    hasPopup: undefined,
    expanded: undefined,
    selected: undefined
  }
)

const emit = defineEmits<{
  (e: 'afterShow'): void
  (e: 'afterHide'): void
  (e: 'click', event: MouseEvent): void
}>()

const variantMapping = {
  default: '',
  extraPadding: 'p-2',
  uiButtonBar: 'px-[6px] py-[6px]',
  outlineSafariPatch: 'p-1 -outline-offset-4 focus:outline focus:outline-2' // use for elements that do not show focus ring in Safari
}

const isMobile = computed(() => useDetectBreakpoint('xs'))
const hovered = ref(false)
const pressed = ref(false)
const tooltipShowing = ref(false)

const tooltipTogglesOnClick = computed(
  () => isMobile.value && !!props.tooltipOnMobile
)

const iconWeight = computed<'duotone' | 'regular' | 'fill'>(() => {
  if (!props.disabled) {
    if (tooltipTogglesOnClick.value) {
      return tooltipShowing.value ? 'fill' : 'regular'
    }
    if (hovered.value) return 'duotone'
    if (pressed.value) return 'fill'
  }
  return 'regular'
})

const onMouseLeave = () => {
  hovered.value = false
  pressed.value = false
}

const onAfterShow = () => {
  tooltipShowing.value = true
  emit('afterShow')
}

const onAfterHide = () => {
  tooltipShowing.value = false
  emit('afterHide')
}
</script>

<template>
  <UIToolTip
    :text="tooltipText"
    :direction="tooltipDirection"
    :alignment="tooltipAlignment"
    :text-size="tooltipTextSize"
    :max-width-px="tooltipMaxWidthPx"
    :toggle-on-click="tooltipTogglesOnClick"
    :disabled="disabled || (isMobile && !tooltipOnMobile)"
    :delay-tag="tooltipDelayTag"
    :variant="tooltipVariant"
    class="group rounded"
    @after-show="onAfterShow"
    @after-hide="onAfterHide"
  >
    <div>
      <button
        class="relative block text-emphasis rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 [&_svg]:pointer-events-none"
        :class="variantMapping[variant]"
        :disabled="disabled"
        :type="buttonType"
        :aria-label="label ?? tooltipText"
        :aria-haspopup="hasPopup"
        :aria-expanded="expanded"
        :aria-pressed="selected"
        :aria-controls="ariaControls"
        @click="$emit('click', $event)"
        @mouseenter="hovered = true"
        @mouseleave="onMouseLeave"
        @mousedown="pressed = true"
        @mouseup="pressed = false"
        @touchstart="pressed = true"
        @touchend="pressed = false"
      >
        <slot
          :hovered="hovered"
          :pressed="pressed"
          :default-icon-weight="iconWeight"
        >
          <PhQuestion size="20" :weight="iconWeight" />
        </slot>
      </button>
    </div>
  </UIToolTip>
</template>
